// Shared component: lets the admin set values for static merge fields used in campaign templates.
// Personal fields ({{firstName}}, {{fullName}}) and {{orgName}} are resolved automatically — only
// campaign-specific static fields are shown here.

import React from 'react';

// Which merge fields are auto-resolved (no admin input needed)
const AUTO_FIELDS = new Set(['{{firstName}}', '{{fullName}}', '{{orgName}}',
  '{{changeSummary}}', '{{keepLink}}', '{{cancelLink}}', '{{manageLink}}']);

const STATIC_MERGE_FIELDS = [
  { key: 'eventName',      tag: '{{eventName}}',      label: 'Event Name' },
  { key: 'eventDate',      tag: '{{eventDate}}',      label: 'Event Date' },
  { key: 'eventLocation',  tag: '{{eventLocation}}',  label: 'Event Location' },
  { key: 'month',          tag: '{{month}}',          label: 'Month' },
  { key: 'programName',    tag: '{{programName}}',    label: 'Programme Name' },
  { key: 'shiftDate',      tag: '{{shiftDate}}',      label: 'Shift Date' },
  { key: 'hoursLogged',    tag: '{{hoursLogged}}',    label: 'Hours Logged' },
  { key: 'milestoneLabel', tag: '{{milestoneLabel}}', label: 'Milestone Label' },
];

/**
 * Detects which static merge field tags are present in the given text so we only
 * show inputs for the fields actually referenced in the campaign content.
 */
function detectUsedFields(text = '') {
  return STATIC_MERGE_FIELDS.filter(f => text.includes(f.tag));
}

/**
 * Props:
 *   values   — { eventName: '', eventDate: '', ... }
 *   onChange — called with updated values object
 *   body     — the campaign body/title text (used to detect which fields to show)
 *   showAll  — if true, show all static fields regardless of usage
 */
export default function MergeFieldValuesSection({ values = {}, onChange, body = '', showAll = false }) {
  const usedFields = showAll ? STATIC_MERGE_FIELDS : detectUsedFields(body);

  if (usedFields.length === 0) {
    return (
      <div className="bb-comm-section-card" style={{ background: '#F9FAFB' }}>
        <div className="bb-comm-card-title" style={{ color: '#6B7280' }}>Merge Fields</div>
        <p style={{ fontSize: 12, color: '#9CA3AF', margin: 0 }}>
          Use tags like <code style={{ background: '#E5E7EB', borderRadius: 3, padding: '1px 5px' }}>{'{{eventName}}'}</code> in your message to insert dynamic values.
          Fields like <code style={{ background: '#E5E7EB', borderRadius: 3, padding: '1px 5px' }}>{'{{firstName}}'}</code> and <code style={{ background: '#E5E7EB', borderRadius: 3, padding: '1px 5px' }}>{'{{orgName}}'}</code> are filled automatically.
        </p>
      </div>
    );
  }

  const set = (key, val) => onChange({ ...values, [key]: val });

  return (
    <div className="bb-comm-section-card">
      <div className="bb-comm-card-title">Merge Field Values</div>
      <p style={{ fontSize: 12, color: '#6B7280', marginTop: 0, marginBottom: 12 }}>
        Set the values for the merge fields used in this campaign. These apply to all recipients.
        Personal fields like <code style={{ background: '#E5E7EB', borderRadius: 3, padding: '1px 5px' }}>{'{{firstName}}'}</code> are filled automatically per recipient.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px 16px' }}>
        {usedFields.map(f => (
          <div key={f.key} className="bb-form-group" style={{ margin: 0 }}>
            <label className="bb-form-label" style={{ marginBottom: 3 }}>
              {f.label}
              <code style={{ marginLeft: 6, fontSize: 10, background: '#E5E7EB', borderRadius: 3, padding: '1px 5px', fontWeight: 400 }}>{f.tag}</code>
            </label>
            <input
              className="bb-form-input"
              type="text"
              placeholder={`Enter ${f.label.toLowerCase()}…`}
              value={values[f.key] || ''}
              onChange={e => set(f.key, e.target.value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
